import React from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import {
  ArrowLeftIcon,
  TrashIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const ContactDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const contactsListSearch = (location.state as { contactsListSearch?: string } | null)?.contactsListSearch;
  const contactsListPath =
    contactsListSearch !== undefined ? `/contacts${contactsListSearch}` : '/contacts';
  const queryClient = useQueryClient();

  // Fetch contact details
  const { data: contact, isLoading, error } = useQuery({
    queryKey: ['contact', id],
    queryFn: async () => {
      const response = await api.get(`/contacts/${id}`);
      return response.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  // Get all contacts for navigation
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await api.get('/contacts');
      return response.data;
    },
  });

  // Delete contact mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/contacts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success('Contact deleted successfully');
      navigate(contactsListPath);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete contact');
    }
  });

  const handleDelete = () => {
    confirmAlert({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete the contact "${contact?.name}"?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => deleteMutation.mutate()
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };



  // Navigation logic
  const contacts = contactsData?.contacts || [];
  const currentIndex = contacts.findIndex((c: any) => c.id === id);
  const previousContact = currentIndex > 0 ? contacts[currentIndex - 1] : null;
  const nextContact = currentIndex < contacts.length - 1 ? contacts[currentIndex + 1] : null;

  const navigateToContact = (contactId: string) => {
    navigate(`/contacts/${contactId}`, { state: location.state });
  };

  if (isLoading) return <div className="p-4">Loading contact details...</div>;
  if (error) return <div className="p-4 text-red-600">Error loading contact details</div>;
  if (!contact) return <div className="p-4">Contact not found</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(contactsListPath)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Contacts
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-gray-500" />
                  </div>
                </div>
                <div className="ml-4">
                  <h1 className="text-xl font-semibold text-gray-900">{contact.name}</h1>
                  <p className="text-sm text-gray-500">{contact.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to={`/contacts/${id}/edit`}
                  state={location.state}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Edit Contact
                </Link>
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Row 1: First Name and Last Name */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={contact.firstName || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={contact.lastName || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Display Name and Email */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={contact.displayName || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={contact.email}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Business Phone and Mobile Phone */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      value={contact.businessPhone || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      value={contact.mobilePhone || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Customer and Primary Contact */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer
                    </label>
                  </div>
                  <div className="col-span-9">
                    {contact.customer ? (
                      <Link
                        to={`/customers/${contact.customer.id}`}
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-indigo-600 hover:text-indigo-800 text-sm h-8 inline-block truncate"
                      >
                        {contact.customer.accountName}
                      </Link>
                    ) : (
                      <input
                        type="text"
                        value=""
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Primary Contact
                    </label>
                  </div>
                  <div className="col-span-9">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={contact.isPrimary || false}
                        readOnly
                        disabled
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        {contact.isPrimary ? 'Yes - Primary contact for this customer' : 'No'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Region */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Region
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={contact.region || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Status */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={
                        contact.status
                          ? contact.status.charAt(0).toUpperCase() + contact.status.slice(1)
                          : ''
                      }
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6"></div>
            </div>

            {/* Row 7: Active */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      checked={contact.active || false}
                      readOnly
                      disabled
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:bg-gray-100"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label className="font-medium text-gray-700">
                      Active
                    </label>
                    <p className="text-gray-500">Contact can access the system</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination Navigation */}
        {contacts.length > 0 && (
          <div className="mt-6 bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => previousContact && navigateToContact(previousContact.id)}
                  disabled={!previousContact}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => nextContact && navigateToContact(nextContact.id)}
                  disabled={!nextContact}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing contact{' '}
                    <span className="font-medium">{currentIndex + 1}</span> of{' '}
                    <span className="font-medium">{contacts.length}</span>
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => previousContact && navigateToContact(previousContact.id)}
                      disabled={!previousContact}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                      {currentIndex + 1} of {contacts.length}
                    </span>
                    <button
                      onClick={() => nextContact && navigateToContact(nextContact.id)}
                      disabled={!nextContact}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactDetail;